import type {
  DailyPayRow,
  DailyPayUploadPreview,
  DailyPayValueChange,
} from '@/app/features/employee/emp-payroll';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

/**
 * Reads a filled sheet in the browser and works out what it would change.
 *
 * The server has no dry-run endpoint, so this exists purely to warn before the
 * file is sent: the upload itself is still parsed and applied server side, and
 * this never writes anything.
 */

/** `1` and `0` are what the sheet asks for; the words stay accepted. */
const YES = new Set(['1', 'yes', 'y', 'true', 'present', 'p']);
const NO = new Set(['0', 'no', 'n', 'false', 'absent', 'a']);

/** Column headers that are never a day cell. */
const IDENTITY_COLUMNS = new Set([
  'Employee ID',
  'EmployeeID',
  'Name',
  'Employee Name',
  'Note',
  'Date',
]);

const toDate = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (!parsed) return null;
    return dayjs(new Date(parsed.y, parsed.m - 1, parsed.d)).format(
      'YYYY-MM-DD',
    );
  }
  const parsed = dayjs(String(value).trim());
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

const toAnswer = (value: unknown): boolean | null | 'INVALID' => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return null; // blank: the sheet says nothing about this day
  }
  const text = String(value).trim().toLowerCase();
  if (YES.has(text)) return true;
  if (NO.has(text)) return false;
  return 'INVALID';
};

export const previewPaySheetFile = async (
  file: File,
  rows: DailyPayRow[],
): Promise<DailyPayUploadPreview> => {
  const book = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const sheet = book.Sheets[book.SheetNames[0]];
  const sheetRows = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, {
    defval: '',
  });

  const byEmployeeId = new Map(
    rows.map((row) => [String(row.employee_id).trim(), row]),
  );

  const employees = new Set<string>();
  const days = new Set<string>();
  const changes: DailyPayValueChange[] = [];
  const reasons: Record<string, number> = {};
  let entries = 0;
  let failed = 0;

  const reject = (reason: string) => {
    reasons[reason] = (reasons[reason] || 0) + 1;
    failed += 1;
  };

  sheetRows.forEach((sheetRow) => {
    const employeeId = String(sheetRow['Employee ID'] ?? '').trim();
    const row = employeeId ? byEmployeeId.get(employeeId) : undefined;

    if (!row) {
      reject(employeeId ? 'Employee is not on this sheet' : 'Employee ID is required');
      return;
    }

    const readCell = (rawDate: unknown, rawAnswer: unknown) => {
      const date = toDate(rawDate);
      if (!date) return reject('Invalid or missing date');

      const answer = toAnswer(rawAnswer);
      if (answer === null) return; // blank leaves the day as it is
      if (answer === 'INVALID') return reject('Value must be 1 or 0');

      entries += 1;
      employees.add(row.user_id);
      days.add(date);

      const current = row.entries.find((entry) => entry.date === date);
      // Only a stored answer that disagrees with the file is a change.
      if (current && Boolean(current.is_payable) !== answer) {
        changes.push({
          employee_id: row.employee_id,
          name: row.name,
          date,
          from: Boolean(current.is_payable),
          to: answer,
        });
      }
    };

    if ('Date' in sheetRow) {
      readCell(
        sheetRow['Date'],
        sheetRow['Payable (1/0)'] ??
          sheetRow['Present (1/0)'] ??
          sheetRow['Payable'] ??
          sheetRow['Present'],
      );
      return;
    }

    // Whole-month sheet: every column whose header reads as a date is a day.
    Object.keys(sheetRow)
      .filter((column) => !IDENTITY_COLUMNS.has(column))
      .filter((column) => toDate(column))
      .forEach((column) => readCell(column, sheetRow[column]));
  });

  return {
    employees: employees.size,
    days: days.size,
    entries,
    overwrites: changes.length,
    failed,
    reasons: Object.entries(reasons).map(
      ([reason, count]) => `${reason} (${count})`,
    ),
    changes: changes.sort(
      (a, b) => a.date.localeCompare(b.date) || a.name.localeCompare(b.name),
    ),
  };
};
